/**
 * EvidenceHintsConfig.jsx
 *
 * Editor for evidence_hints.yaml. Follows the same shell convention as the
 * other configuration pages (Sider + navy Header with breadcrumb +
 * Lock/Save). Adds a Settings (gear) button in the header that opens a
 * modal for editing the two top-level globals (default_evidence_sections,
 * max_codes_per_encounter).
 *
 * Body: card with a left list of codes (ordered by priority_order) and a
 * right editor for the selected code. The selected code's evidence_sections
 * is a DRAG-REORDERABLE list (search sequence matters); topics_and_keywords
 * is a plain list of free-text strings. Adding a new code creates an entry
 * that pre-populates `evidence_sections` with a COPY of
 * default_evidence_sections, and also appends the code to priority_order so
 * it becomes eligible.
 *
 * Page lands locked. Click Lock → unlock → all fields and reorder /
 * add / delete operations become available. Save is a placeholder; all
 * edits are in-memory.
 *
 * Data: fetched from /rules/evidence_hints.yaml.
 *
 * Dependencies:
 *   - js-yaml
 *   - @dnd-kit/core
 *   - @dnd-kit/sortable
 *   - @dnd-kit/utilities
 *   (npm install @dnd-kit/core @dnd-kit/sortable @dnd-kit/utilities)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Empty,
  Spin, Alert, Popconfirm, Modal, Form, AutoComplete, InputNumber,
  message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined, SearchOutlined,
  SettingOutlined, HolderOutlined, CloseOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const EVIDENCE_HINTS_URL = '/rules/evidence_hints.yaml';

// ─── Known section names (autocomplete options) ─────────────────────────
// Sourced from evidence_hints.yaml + section_definitions.json. User can
// also type a custom value — AutoComplete is freeSolo.
const KNOWN_SECTION_NAMES = [
  'assessment_plan',
  'pmh',
  'problem_list',
  'hpi',
  'physical_exam',
  'medications',
  'labs',
  'social_history',
  'family_history',
  'ros',
  'objective',
  'subjective',
  'clinical_history',
];

// ─── SortableSectionRow — one draggable row in an evidence_sections list ─
// Used both in the per-code editor and in the Settings modal for
// default_evidence_sections.
const SortableSectionRow = ({ id, value, onDelete, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    padding: '8px 10px',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Drag handle */}
      <span
        {...listeners}
        style={{
          cursor: disabled ? 'default' : 'grab',
          color: disabled ? '#D1D5DB' : '#6B7280',
          padding: '2px 4px',
          touchAction: 'none',
        }}
        title={disabled ? 'Unlock to reorder' : 'Drag to reorder'}
      >
        <HolderOutlined />
      </span>

      <span style={{
        flex: 1,
        fontFamily: 'monospace',
        fontSize: 13,
        color: '#111827',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </span>

      {!disabled && onDelete && (
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined style={{ color: '#DC2626' }} />}
          onClick={onDelete}
        />
      )}
    </div>
  );
};

// ─── EvidenceSectionsEditor — drag-reorderable list + add row ──────────
// Renders an ordered evidence_sections list with d&d. Used in both the
// per-code editor and the Settings modal.
const EvidenceSectionsEditor = ({ sections, onChange, editMode, label }) => {
  const [addInput, setAddInput] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.indexOf(active.id);
      const newIndex = sections.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(sections, oldIndex, newIndex));
      }
    }
  };

  const handleDeleteAt = (i) => {
    onChange(sections.filter((_, idx) => idx !== i));
  };

  const handleAdd = () => {
    const v = addInput.trim();
    if (!v) return;
    if (sections.includes(v)) {
      message.warning(`"${v}" is already in the list`);
      return;
    }
    onChange([...sections, v]);
    setAddInput('');
  };

  // De-dup section IDs so dnd-kit gets unique keys. (Defensive — shouldn't
  // happen given the duplicate guard above, but YAML may carry duplicates.)
  const uniqueIds = useMemo(() => {
    const seen = new Set();
    return sections.map((s, idx) => {
      const base = s;
      let id = base;
      let n = 1;
      while (seen.has(id)) { id = `${base}__${++n}`; }
      seen.add(id);
      return id;
    });
  }, [sections]);

  const autocompleteOptions = KNOWN_SECTION_NAMES
    .filter(s => !sections.includes(s))
    .filter(s => !addInput || s.includes(addInput.toLowerCase()))
    .map(s => ({ value: s }));

  return (
    <div>
      {label && (
        <Text style={{
          fontSize: 11, fontWeight: 700, color: '#9CA3AF',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'block', marginBottom: 8,
        }}>
          {label} ({sections.length})
        </Text>
      )}

      {sections.length === 0 ? (
        <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', display: 'block', marginBottom: 10 }}>
          No sections defined.
        </Text>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={uniqueIds} strategy={verticalListSortingStrategy}>
            {sections.map((s, idx) => (
              <SortableSectionRow
                key={uniqueIds[idx]}
                id={uniqueIds[idx]}
                value={s}
                disabled={!editMode}
                onDelete={editMode ? () => handleDeleteAt(idx) : null}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {editMode && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <AutoComplete
            value={addInput}
            onChange={setAddInput}
            options={autocompleteOptions}
            placeholder="Section name (e.g., pmh, labs)"
            style={{ flex: 1, fontFamily: 'monospace' }}
            onSelect={() => { /* picked from dropdown — keep typed value */ }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            filterOption={false}
          />
          <Button
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={!addInput.trim()}
            style={{ backgroundColor: addInput.trim() ? '#162349' : undefined, color: addInput.trim() ? 'white' : undefined }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────
const EvidenceHintsConfig = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [yamlData, setYamlData]     = useState(null);  // { default_evidence_sections, max_codes_per_encounter, priority_order, codes }
  const [selectedCode, setSelectedCode] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [editMode, setEditMode]     = useState(false);

  // Add-new-code modal state
  const [addModalOpen, setAddModalOpen]       = useState(false);
  const [newCode, setNewCode]                 = useState('');
  const [newDescription, setNewDescription]   = useState('');
  const [newCodeError, setNewCodeError]       = useState(null);

  // Settings modal state
  const [settingsOpen, setSettingsOpen]       = useState(false);

  // ── Load ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(EVIDENCE_HINTS_URL)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(text => {
        if (cancelled) return;
        const parsed = yaml.load(text) || {};
        const normalized = {
          default_evidence_sections: Array.isArray(parsed.default_evidence_sections)
            ? parsed.default_evidence_sections : [],
          max_codes_per_encounter: parsed.max_codes_per_encounter ?? 5,
          priority_order: Array.isArray(parsed.priority_order) ? parsed.priority_order : [],
          codes: parsed.codes && typeof parsed.codes === 'object' ? parsed.codes : {},
        };
        setYamlData(normalized);

        // Default selection: first code in priority_order that exists in codes,
        // else first key in codes.
        const firstInPriority = normalized.priority_order.find(c => normalized.codes[c]);
        const firstCode = firstInPriority || Object.keys(normalized.codes)[0] || null;
        setSelectedCode(firstCode);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load evidence_hints.yaml:', err);
        setLoadError(err.message || 'Unknown error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Derived: ordered list of codes for the left panel ───────────
  // priority_order defines the canonical order; codes present in `codes`
  // but not in priority_order are appended below.
  const codeIdsOrdered = useMemo(() => {
    if (!yamlData) return [];
    const inPriority = yamlData.priority_order.filter(c => yamlData.codes[c]);
    const inPrioritySet = new Set(inPriority);
    const extra = Object.keys(yamlData.codes).filter(c => !inPrioritySet.has(c));
    return [...inPriority, ...extra];
  }, [yamlData]);

  const filteredCodeIds = useMemo(() => {
    if (!searchText.trim()) return codeIdsOrdered;
    const q = searchText.toLowerCase();
    return codeIdsOrdered.filter(c => {
      if (c.toLowerCase().includes(q)) return true;
      const desc = yamlData?.codes[c]?.description || '';
      return desc.toLowerCase().includes(q);
    });
  }, [codeIdsOrdered, searchText, yamlData]);

  const selectedEntry = selectedCode && yamlData ? yamlData.codes[selectedCode] : null;

  // ── Handlers: lock / save / settings ────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);

  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  // ── Per-code field edits ────────────────────────────────────────
  const updateSelectedEntry = useCallback((patch) => {
    if (!selectedCode || !yamlData) return;
    setYamlData(prev => ({
      ...prev,
      codes: {
        ...prev.codes,
        [selectedCode]: { ...prev.codes[selectedCode], ...patch },
      },
    }));
  }, [selectedCode, yamlData]);

  const handleDescriptionChange = (e) => {
    updateSelectedEntry({ description: e.target.value });
  };

  const handleEvidenceSectionsChange = (newSections) => {
    updateSelectedEntry({ evidence_sections: newSections });
  };

  const handleAddTopic = () => {
    const cur = selectedEntry?.topics_and_keywords || [];
    updateSelectedEntry({ topics_and_keywords: [...cur, ''] });
  };

  const handleUpdateTopic = (idx, value) => {
    const cur = selectedEntry?.topics_and_keywords || [];
    const next = cur.map((t, i) => (i === idx ? value : t));
    updateSelectedEntry({ topics_and_keywords: next });
  };

  const handleRemoveTopic = (idx) => {
    const cur = selectedEntry?.topics_and_keywords || [];
    updateSelectedEntry({ topics_and_keywords: cur.filter((_, i) => i !== idx) });
  };

  // ── Add new code ────────────────────────────────────────────────
  const openAddModal = () => {
    setNewCode('');
    setNewDescription('');
    setNewCodeError(null);
    setAddModalOpen(true);
  };

  const handleAddCodeSubmit = () => {
    const trimmed = newCode.trim();
    if (!trimmed) {
      setNewCodeError('ICD-10 code is required.');
      return;
    }
    if (yamlData.codes[trimmed]) {
      setNewCodeError(`Code "${trimmed}" already exists.`);
      return;
    }

    setYamlData(prev => ({
      ...prev,
      codes: {
        ...prev.codes,
        [trimmed]: {
          description: newDescription.trim(),
          evidence_sections: [...(prev.default_evidence_sections || [])],  // copy of defaults
          topics_and_keywords: [],
        },
      },
      priority_order: prev.priority_order.includes(trimmed)
        ? prev.priority_order
        : [...prev.priority_order, trimmed],
    }));
    setSelectedCode(trimmed);
    setAddModalOpen(false);
  };

  // ── Delete code (removes from codes AND priority_order) ─────────
  const handleDeleteCode = (code) => {
    setYamlData(prev => {
      const nextCodes = { ...prev.codes };
      delete nextCodes[code];
      return {
        ...prev,
        codes: nextCodes,
        priority_order: prev.priority_order.filter(c => c !== code),
      };
    });
    if (selectedCode === code) {
      const remaining = codeIdsOrdered.filter(c => c !== code);
      setSelectedCode(remaining[0] || null);
    }
  };

  // ── Settings modal (global defaults) ────────────────────────────
  const handleDefaultsChange = (newSections) => {
    setYamlData(prev => ({ ...prev, default_evidence_sections: newSections }));
  };

  const handleMaxCodesChange = (val) => {
    setYamlData(prev => ({ ...prev, max_codes_per_encounter: val ?? 1 }));
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider ──────────────────────────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'fixed',
          left: 0, top: 0, bottom: 0, zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column', justifyContent: 'space-between',
          height: '100%', alignItems: 'center', padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white', fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Evidence Hints
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Tooltip title="Edit global defaults (default_evidence_sections, max_codes_per_encounter)">
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsOpen(true)}
                style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
              >
                Settings
              </Button>
            </Tooltip>

            <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
              <Button
                icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                onClick={handleLockToggle}
                style={{
                  backgroundColor: editMode ? '#FEF3C7' : 'white',
                  color: '#162349',
                  fontWeight: 600,
                  borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                }}
              >
                {editMode ? 'Unlocked' : 'Locked'}
              </Button>
            </Tooltip>

            <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={!editMode}
                style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </Header>

        {/* ── Content ───────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading evidence hints…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={<span>Could not load <code>{EVIDENCE_HINTS_URL}</code>. ({loadError})</span>}
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && yamlData && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              minHeight: 'calc(100vh - 64px - 56px)',
            }}>

              {/* ── Left: code list ─────────────────────────────── */}
              <div style={{
                width: 320, flexShrink: 0,
                borderRight: '1px solid #E5E7EB',
                display: 'flex', flexDirection: 'column',
                background: '#FAFAFA',
              }}>
                {/* Add-new */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Tooltip title={editMode ? '' : 'Unlock to add new codes'}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={openAddModal}
                      disabled={!editMode}
                      block
                      style={{
                        backgroundColor: editMode ? '#162349' : undefined,
                        color: editMode ? 'white' : undefined,
                        fontWeight: 600,
                      }}
                    >
                      Add new code
                    </Button>
                  </Tooltip>
                </div>

                {/* Search */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                    placeholder="Search code or description…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                  />
                </div>

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {filteredCodeIds.length === 0 ? (
                    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {searchText ? `No codes match "${searchText}"` : 'No codes defined.'}
                      </Text>
                    </div>
                  ) : (
                    filteredCodeIds.map(code => {
                      const isSelected = code === selectedCode;
                      const desc = yamlData.codes[code]?.description || '';
                      return (
                        <div
                          key={code}
                          onClick={() => setSelectedCode(code)}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            background: isSelected ? '#E0E7FF' : 'transparent',
                            borderLeft: isSelected ? '3px solid #162349' : '3px solid transparent',
                            borderBottom: '1px solid #F0F0F0',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 8,
                            transition: 'background 0.12s',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#F0F4FF'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <span style={{
                              fontFamily: 'monospace',
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#162349',
                              display: 'block',
                            }}>
                              {code}
                            </span>
                            <span style={{
                              fontSize: 12,
                              color: '#6B7280',
                              display: 'block',
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {desc || <em style={{ color: '#D1D5DB' }}>(no description)</em>}
                            </span>
                          </div>

                          {editMode && (
                            <Popconfirm
                              title="Delete this code?"
                              description={`Remove "${code}" and its hints, including from priority_order?`}
                              onConfirm={(e) => { if (e?.stopPropagation) e.stopPropagation(); handleDeleteCode(code); }}
                              onCancel={(e) => { if (e?.stopPropagation) e.stopPropagation(); }}
                              okText="Delete"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined style={{ color: '#DC2626' }} />}
                                onClick={e => e.stopPropagation()}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer count */}
                <div style={{ padding: '10px 16px', borderTop: '1px solid #E5E7EB', background: 'white' }}>
                  <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {filteredCodeIds.length} of {codeIdsOrdered.length} codes
                  </Text>
                </div>
              </div>

              {/* ── Right: editor for the selected code ─────────── */}
              <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
                {!selectedCode || !selectedEntry ? (
                  <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <Empty description="Select a code to view its evidence hints." />
                  </div>
                ) : (
                  <>
                    {/* Code heading */}
                    <div style={{ marginBottom: 24 }}>
                      <Text style={{
                        fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>
                        ICD-10 code
                      </Text>
                      <Title level={3} style={{ margin: '4px 0 0 0', fontFamily: 'monospace', color: '#111827' }}>
                        {selectedCode}
                      </Title>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: 28 }}>
                      <Text style={{
                        fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        display: 'block', marginBottom: 6,
                      }}>
                        Description
                      </Text>
                      {editMode ? (
                        <TextArea
                          value={selectedEntry.description || ''}
                          onChange={handleDescriptionChange}
                          rows={2}
                          placeholder="Official ICD-10-CM description"
                        />
                      ) : (
                        <Text style={{ fontSize: 14, color: '#1F2937', display: 'block', whiteSpace: 'pre-wrap' }}>
                          {selectedEntry.description || (
                            <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>(no description)</span>
                          )}
                        </Text>
                      )}
                    </div>

                    {/* Evidence Sections (drag-reorderable) */}
                    <div style={{ marginBottom: 28 }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text style={{
                          fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                        }}>
                          Evidence sections — search sequence
                        </Text>
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginTop: 2 }}>
                          {editMode
                            ? 'Drag the handle to reorder. Top of list is searched first.'
                            : 'Top of list is searched first. Unlock to edit or reorder.'}
                        </Text>
                      </div>
                      <EvidenceSectionsEditor
                        sections={selectedEntry.evidence_sections || []}
                        onChange={handleEvidenceSectionsChange}
                        editMode={editMode}
                      />
                    </div>

                    {/* Topics and keywords */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text style={{
                          fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                        }}>
                          Topics &amp; keywords ({(selectedEntry.topics_and_keywords || []).length})
                        </Text>
                        {editMode && (
                          <Button size="small" icon={<PlusOutlined />} onClick={handleAddTopic}>
                            Add topic
                          </Button>
                        )}
                      </div>

                      {(selectedEntry.topics_and_keywords || []).length === 0 ? (
                        <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                          No topics or keywords defined.
                        </Text>
                      ) : editMode ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {(selectedEntry.topics_and_keywords || []).map((t, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                              <TextArea
                                value={t}
                                onChange={(e) => handleUpdateTopic(idx, e.target.value)}
                                placeholder="Describe a topic, term, drug class, or finding pattern…"
                                autoSize={{ minRows: 1, maxRows: 4 }}
                              />
                              <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleRemoveTopic(idx)}
                                danger
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul style={{ paddingLeft: 22, margin: 0 }}>
                          {(selectedEntry.topics_and_keywords || []).map((t, idx) => (
                            <li key={idx} style={{
                              fontSize: 14, color: '#1F2937',
                              marginBottom: 6, lineHeight: 1.45,
                            }}>
                              {t}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        </Content>
      </Layout>

      {/* ── Add-new-code modal ─────────────────────────────────────────── */}
      <Modal
        title="Add new code"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddCodeSubmit}
        okText="Add"
        cancelText="Cancel"
        destroyOnClose
        width={560}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="ICD-10 code"
            required
            validateStatus={newCodeError ? 'error' : undefined}
            help={newCodeError || 'e.g. M81.0, E11.40, F32.A'}
          >
            <Input
              value={newCode}
              onChange={e => { setNewCode(e.target.value); if (newCodeError) setNewCodeError(null); }}
              placeholder="ICD-10 code"
              style={{ fontFamily: 'monospace', fontWeight: 600 }}
              autoFocus
            />
          </Form.Item>
          <Form.Item label="Description">
            <TextArea
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              rows={3}
              placeholder="Official ICD-10-CM description"
            />
          </Form.Item>
          {yamlData && (
            <div style={{
              padding: '10px 12px',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: 6,
              marginTop: 8,
            }}>
              <Text style={{ fontSize: 12, color: '#6B7280' }}>
                On save, evidence_sections will be initialized to the global defaults
                ({yamlData.default_evidence_sections.length} section{yamlData.default_evidence_sections.length === 1 ? '' : 's'}).
                The code will also be appended to <code>priority_order</code> so it becomes eligible.
              </Text>
            </div>
          )}
        </Form>
      </Modal>

      {/* ── Settings modal (global defaults) ──────────────────────────── */}
      <Modal
        title="Global defaults"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        onOk={() => setSettingsOpen(false)}
        okText="Done"
        cancelText={null}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={580}
      >
        {yamlData && (
          <div style={{ marginTop: 12 }}>
            <Text style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 16 }}>
              These values apply globally. <code>default_evidence_sections</code> is
              the starting list used whenever a new code is added.
              {!editMode && ' Unlock the page to edit.'}
            </Text>

            {/* Max codes */}
            <div style={{ marginBottom: 24 }}>
              <Text style={{
                fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                display: 'block', marginBottom: 6,
              }}>
                Max codes per encounter
              </Text>
              <InputNumber
                value={yamlData.max_codes_per_encounter}
                onChange={handleMaxCodesChange}
                min={1}
                max={50}
                disabled={!editMode}
                style={{ width: 120 }}
              />
              <Text style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 12 }}>
                Single LLM call carries up to this many codes.
              </Text>
            </div>

            {/* Default evidence sections */}
            <EvidenceSectionsEditor
              sections={yamlData.default_evidence_sections}
              onChange={handleDefaultsChange}
              editMode={editMode}
              label="Default evidence sections"
            />

            {!editMode && (
              <div style={{ marginTop: 16, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6 }}>
                <Text style={{ fontSize: 12, color: '#6B7280' }}>
                  Page is locked (read-only). Close this dialog and click the Lock button to enable edits.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>

    </Layout>
  );
};

export default EvidenceHintsConfig;